import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {makeReviewProcessesColumn} from 'nav/expanded/FolderItemListingRenderer';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
  ListingTypes,
} from 'nav/expanded/ListingRendererConstants';
import {getCountsString} from 'notebook/entry-templates/collections/TemplateCollectionsPage';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import licenses from 'user/Licenses';
import {BasicEvent} from 'util/Types';

export default class CollectionListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.COLLECTION> {
  listingRendererType = ListingRendererType.COLLECTION;

  getNonPluginColumnConfigurations(
    items: Array<ListingTypes[ListingRendererType.COLLECTION]>,
    extras: unknown
  ): Array<ColumnConfiguration<ListingTypes[ListingRendererType.COLLECTION]>> {
    return [
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          accessorKey: ColumnAccessorKey.NAME,
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
        },
        render: (item) => <div>{item.name}</div>,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.OWNER,
          header: 'Owner',
          flexGrow: 1,
          width: 200,
          isCollapsible: false,
        },
        render: (item) => <div>{item.ownerJson.name}</div>,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.COLLECTION_COUNTS,
          header: 'Counts',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => getCountsString(item.counts),
      },
      ...(licenses.hasLicense('LICENSE_REVIEWS') && BenchlingConfig.ELN_ENABLE_TEMPLATE_REVIEWS
        ? [
            makeReviewProcessesColumn<ListingTypes[ListingRendererType.COLLECTION]>({
              // Names are loaded up-front by TemplateCollectionListingGraphQL, so render them directly
              // instead of resolving ids with a per-row query.
              getLifecycleNames: (collection) =>
                collection.reviewLifecycles.map((reviewLifecycle) => reviewLifecycle.currentVersion.name),
              isApplicable: () => true,
            }),
          ]
        : []),
    ];
  }

  getNonPluginRowContentHeight(): number {
    return TABLE_ROW_BODY_HEIGHT;
  }

  openContextMenu(
    item: ListingTypes[ListingRendererType.COLLECTION],
    e: BasicEvent,
    options: unknown
  ): void {}

  renderIcon(item: ListingTypes[ListingRendererType.COLLECTION], options: unknown): JSX.Element {
    return (
      <BenchlingIcon icon={ModelTypeConstants.TEMPLATE_COLLECTION} className="scrollableTable-cell-icon" />
    );
  }
}
